import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../../../../lib/api';
import { formatCurrency } from '../../../../utils/formatters';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type StatoFatturaAcconto = 'PROFORMA' | 'SDI';

interface FatturaItem {
  id: number;
  numero: string | null;
  statoFattura: StatoFatturaAcconto;
  data: string;
  imponibile: string;
  iva: string;
  totale: string;
  clienteRagioneSociale: string | null;
  proformaId: number | null;
  proformaNumero: string | null;
  ordineId: number | null;
}

interface TabFattureProps {
  commessaId: number;
  societaId: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATO_LABEL: Record<StatoFatturaAcconto, string> = {
  PROFORMA: 'Proforma',
  SDI: 'Inviata SDI',
};

const STATO_CLS: Record<StatoFatturaAcconto, string> = {
  PROFORMA: 'bop-badge-warning',
  SDI: 'bop-badge-success',
};

const fmtDate = (d: string | null | undefined) =>
  d ? new Date(d).toLocaleDateString('it-IT') : '—';

/**
 * TabFatture — vista filtrata della lista fatture ristretta alla commessa.
 * Consuma GET /api/fatture-acconto con societaId + commessaId pre-applicati.
 * Le fatture non hanno una pagina di dettaglio dedicata: il click su una riga
 * apre la proforma acconto di origine in nuova tab.
 */
export default function TabFatture({ commessaId, societaId }: TabFattureProps) {
  const [items, setItems] = useState<FatturaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      // @api GET /api/fatture-acconto?societaId={id}&commessaId={cid} -> {"success":true,"data":[{"id":"number","numero":"string|null","statoFattura":"string","data":"string","imponibile":"string","iva":"string","totale":"string","clienteRagioneSociale":"string|null","proformaId":"number|null","proformaNumero":"string|null","ordineId":"number|null"}]}
      const res = await apiFetch<{ success: boolean; data: FatturaItem[] }>(
        `/api/fatture-acconto?societaId=${societaId}&commessaId=${commessaId}`
      );
      setItems(res.data || []);
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [societaId, commessaId]);

  useEffect(() => { load(); }, [load]);

  const apriProforma = (id: number | null) => {
    if (id) window.open(`/commerciale/attivo/acconti/${id}`, '_blank', 'noopener,noreferrer');
  };

  const totImponibile = items.reduce((s, f) => s + Number(f.imponibile), 0);
  const totIva        = items.reduce((s, f) => s + Number(f.iva), 0);
  const totTotale     = items.reduce((s, f) => s + Number(f.totale), 0);

  if (loading) {
    return <div className="bop-text-muted bop-tab-preventivi__loading">Caricamento fatture…</div>;
  }
  if (error) {
    return <div className="bop-text-danger bop-tab-preventivi__error">{error}</div>;
  }

  return (
    <div className="bop-tab-preventivi">
      <div className="bop-page-header bop-tab-preventivi__header">
        <h2 className="bop-page-title bop-tab-preventivi__title">Fatture della commessa</h2>
      </div>

      {items.length === 0 ? (
        <div className="bop-empty-state bop-tab-preventivi__empty">
          Nessuna fattura su questa commessa.
        </div>
      ) : (
        <>
          <div className="bop-sal__kpi-row">
            <div>
              <span className="bop-sal__kpi-label">Imponibile:</span>
              <span className="bop-sal__kpi-value--total">{formatCurrency(totImponibile)}</span>
            </div>
            <div>
              <span className="bop-sal__kpi-label">IVA:</span>
              <span className="bop-sal__kpi-value--total">{formatCurrency(totIva)}</span>
            </div>
            <div>
              <span className="bop-sal__kpi-label">Totale:</span>
              <span className="bop-sal__kpi-value--approved">{formatCurrency(totTotale)}</span>
            </div>
            <span className="bop-sal__kpi-count">({items.length} fatture)</span>
          </div>

          <div className="bop-card bop-tab-preventivi__card">
            <table className="bop-table bop-table--clickable bop-tab-preventivi__table">
              <thead>
                <tr>
                  <th>Numero</th>
                  <th>Stato</th>
                  <th>Proforma</th>
                  <th>Ordine</th>
                  <th>Cliente</th>
                  <th className="bop-text-right">Imponibile</th>
                  <th className="bop-text-right">IVA</th>
                  <th className="bop-text-right">Totale</th>
                  <th>Data</th>
                </tr>
              </thead>
              <tbody>
                {items.map(f => (
                  <tr key={f.id}
                      className="bop-tab-preventivi__row"
                      onClick={() => apriProforma(f.proformaId)}>
                    <td>{f.numero || <span className="bop-text-muted">—</span>}</td>
                    <td>
                      <span className={`bop-badge ${STATO_CLS[f.statoFattura]}`}>
                        {STATO_LABEL[f.statoFattura]}
                      </span>
                    </td>
                    <td>
                      {f.proformaNumero
                        ? <span>{f.proformaNumero}</span>
                        : <span className="bop-text-muted">—</span>}
                    </td>
                    <td>
                      {f.ordineId
                        ? <span>#{f.ordineId}</span>
                        : <span className="bop-text-muted">—</span>}
                    </td>
                    <td>{f.clienteRagioneSociale || <span className="bop-text-muted">—</span>}</td>
                    <td className="bop-text-right">{formatCurrency(Number(f.imponibile))}</td>
                    <td className="bop-text-right">{formatCurrency(Number(f.iva))}</td>
                    <td className="bop-text-right">{formatCurrency(Number(f.totale))}</td>
                    <td>{fmtDate(f.data)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
